<template>
  <div class="pie-chart-container">
    <ECharts
      ref="pieChart"
      :option="chartOption"
      :width="width"
      :height="height"
      :autoresize="autoresize"
    />
  </div>
</template>

<script>
import ECharts from './ECharts.vue'

export default {
  name: 'PieChart',
  components: {
    ECharts
  },
  props: {
    // 数据
    data: {
      type: Array,
      required: true
    },
    // 图表宽度
    width: {
      type: String,
      default: '100%'
    },
    // 图表高度
    height: {
      type: String,
      default: '400px'
    },
    // 标题
    title: {
      type: String,
      default: ''
    },
    // 是否显示图例
    showLegend: {
      type: Boolean,
      default: true
    },
    // 图例位置
    legendPosition: {
      type: String,
      default: 'bottom'
    },
    // 是否自动调整大小
    autoresize: {
      type: Boolean,
      default: true
    },
    // 是否显示工具提示
    showTooltip: {
      type: Boolean,
      default: true
    },
    // 饼图半径
    radius: {
      type: [Number, String, Array],
      default: '60%'
    },
    // 饼图中心位置
    center: {
      type: Array,
      default: () => ['50%', '50%']
    },
    // 主题颜色
    color: {
      type: Array,
      default: () => ['#409EFF', '#67C23A', '#E6A23C', '#F56C6C', '#909399', '#722ED1', '#13C2C2', '#EB2F96']
    },
    // 动画配置
    animation: {
      type: Boolean,
      default: true
    },
    // 动画时长
    animationDuration: {
      type: Number,
      default: 1000
    },
    // 数据标签配置
    label: {
      type: Object,
      default: () => ({
        show: true,
        formatter: '{b}: {c} ({d}%)'
      })
    },
    // 是否显示百分比
    showPercent: {
      type: Boolean,
      default: true
    },
    // 是否显示空心饼图
    donut: {
      type: Boolean,
      default: false
    },
    // 空心饼图内半径
    donutRadius: {
      type: [Number, String],
      default: '40%'
    },
    // 自定义工具提示格式化函数(params) => html字符串, 不传使用默认格式
    tooltipFormatter: {
      type: Function,
      default: null
    }
  },
  computed: {
    // 图表配置项
    chartOption() {
      // 处理饼图半径
      let radius = this.radius
      if (this.donut) {
        radius = [this.donutRadius, this.radius]
      }
      
      return {
        title: {
          text: this.title,
          left: 'center',
          textStyle: {
            fontSize: 16,
            fontWeight: 'normal'
          }
        },
        tooltip: {
          trigger: 'item',
          show: this.showTooltip,
          formatter: this.tooltipFormatter || this.defaultTooltipFormatter
        },
        legend: {
          orient: 'horizontal',
          bottom: this.legendPosition === 'bottom' ? 0 : undefined,
          top: this.legendPosition === 'top' ? 0 : undefined,
          left: this.legendPosition === 'left' ? 0 : undefined,
          right: this.legendPosition === 'right' ? 0 : undefined,
          data: this.data.map(item => item.name),
          show: this.showLegend
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: this.showLegend && this.legendPosition === 'bottom' ? '25%' : '3%',
          top: this.title ? (this.showLegend && this.legendPosition === 'top' ? '25%' : '15%') : (this.showLegend && this.legendPosition === 'top' ? '20%' : '3%'),
          containLabel: true
        },
        series: [
          {
            name: this.title,
            type: 'pie',
            radius: radius,
            center: this.center,
            data: this.data,
            animation: this.animation,
            animationDuration: this.animationDuration,
            label: {
              show: this.label.show,
              formatter: this.label.formatter,
              color: '#333'
            },
            emphasis: {
              itemStyle: {
                shadowBlur: 10,
                shadowOffsetX: 0,
                shadowColor: 'rgba(0, 0, 0, 0.5)'
              }
            },
            ...this.label
          }
        ]
      }
    }
  },
  methods: {
    // 默认工具提示格式化
    defaultTooltipFormatter(params) {
      const { name, value, percent } = params
      return `${name}<br/>${value} (${percent}%)`
    },
    // 调整图表大小
    resize() {
      if (this.$refs.pieChart) {
        this.$refs.pieChart.resize()
      }
    },
    // 获取图表实例
    getChartInstance() {
      if (this.$refs.pieChart) {
        return this.$refs.pieChart.getChartInstance()
      }
      return null
    },
    // 保存图表为图片
    saveAsImage(params) {
      if (this.$refs.pieChart) {
        return this.$refs.pieChart.saveAsImage(params)
      }
      return null
    },
    // 开始图表动画
    startAnimation() {
      if (this.$refs.pieChart) {
        this.$refs.pieChart.startAnimation()
      }
    },
    // 停止图表动画
    stopAnimation() {
      if (this.$refs.pieChart) {
        this.$refs.pieChart.stopAnimation()
      }
    }
  }
}
</script>

<style scoped>
.pie-chart-container {
  width: 100%;
  height: 100%;
}
</style>